<template>
    <div>
        <h2>Choose your MLS associations</h2>

        <p>Select each MLS you're a member of, and enter your agent ID for that association.</p>

        <p v-if="perAdditionalFee > 0">
            <em>First MLS is included. Each additional MLS is ${{ formatPrice(perAdditionalFee) }}/mo.</em>
        </p>

        <div
            v-for="assoc in state.catalog.associations"
            :key="assoc.id"
            class="signup-form__field"
        >
            <label class="signup-choice">
                <input
                    type="checkbox"
                    class="signup-choice__control"
                    :checked="isSelected(assoc.id)"
                    @change="toggleAssociation(assoc.id)"
                />&nbsp;
                <span class="signup-choice__label">
                    {{ assoc.name }}
                    <small v-if="feeSummary(assoc)" class="signup-choice__meta">— {{ feeSummary(assoc) }}</small>
                </span>
            </label>

            <div v-if="isSelected(assoc.id)" style="margin-top: 0.5rem">
                <label :for="'signup-agentid-' + assoc.id" class="signup-form__label">
                    Your agent ID for {{ assoc.name }}
                    <span class="signup-form__required">*</span>
                </label>
                <input
                    type="text"
                    class="signup-form__input"
                    :id="'signup-agentid-' + assoc.id"
                    :value="agentIdFor(assoc.id)"
                    @input="setAgentId(assoc.id, $event.target.value)"
                    required
                />
                <span
                    v-if="error(`associations.${selectedIndex(assoc.id)}.agentId`)"
                    class="signup-form__error"
                    role="alert"
                >
                    {{ error(`associations.${selectedIndex(assoc.id)}.agentId`) }}
                </span>
            </div>
        </div>

        <p v-if="state.catalog.associations.length === 0">
            <em>No MLS associations are currently open for signup. Please contact us directly.</em>
        </p>

        <div class="signup-form__field">
            <label class="signup-choice">
                <input type="checkbox" class="signup-choice__control" v-model="state.form.otherSelected" />&nbsp;
                <span class="signup-choice__label">
                    Other (not listed)
                    <small class="signup-choice__meta">Tell us which MLS — we'll confirm if we can support it.</small>
                </span>
            </label>

            <div v-if="state.form.otherSelected" style="margin-top: 0.5rem">
                <label for="signup-other-association" class="signup-form__label">
                    Which MLS? <span class="signup-form__required">*</span>
                </label>
                <input
                    type="text"
                    class="signup-form__input"
                    id="signup-other-association"
                    v-model="state.form.otherAssociationName"
                />
                <span v-if="error('otherAssociationName')" class="signup-form__error" role="alert">
                    {{ error('otherAssociationName') }}
                </span>
            </div>
        </div>

        <span v-if="error('associations')" class="signup-form__error" role="alert">{{ error('associations') }}</span>
    </div>
</template>

<script setup>
import { computed, watch } from 'vue';
import useState from '@/composables/useState';

const { state, toggleAssociation, setAgentId } = useState();

// Pulled from the catalog so it stays in sync with admin-edited plan pricing.
const perAdditionalFee = computed(() => {
    const plan = state.catalog.plans.find((p) => p.code === state.form.planCode);
    return parseFloat(plan?.additionalAssociationMonthly || 0);
});

// When the user unchecks Other, clear the typed name so it doesn't sneak into
// the submission. Also covers Back/Next round-trips.
watch(
    () => state.form.otherSelected,
    (v) => {
        if (!v) {
            state.form.otherAssociationName = '';
        }
    },
);

const isSelected = (id) => state.form.associations.some((a) => a.id === id);

const agentIdFor = (id) => state.form.associations.find((a) => a.id === id)?.agentId || '';

const selectedIndex = (id) => state.form.associations.findIndex((a) => a.id === id);

const error = (field) => state.serverErrors[field]?.[0];

const formatPrice = (n) => parseFloat(n).toFixed(2);

////////////////////////////////////////////////////////////////////
/// Build the meta line shown under an association name. Each fee
/// component only appears when > 0, and the admin-authored feeNote is
/// appended whenever present — so a $0 fee shows the note alone
/// ("check with your association"), while a real fee shows
/// "$X/mo, {note}". Returns '' when there's nothing to show.
const feeSummary = (assoc) => {
    const parts = [];
    if (parseFloat(assoc.passthroughFeeMonthly) > 0) {
        parts.push(`$${formatPrice(assoc.passthroughFeeMonthly)}/mo`);
    }
    if (parseFloat(assoc.dataAccessSurchargeMonthly) > 0) {
        parts.push(`$${formatPrice(assoc.dataAccessSurchargeMonthly)}/mo data access`);
    }
    if (parseFloat(assoc.setupPassthroughFee) > 0) {
        parts.push(`$${formatPrice(assoc.setupPassthroughFee)} one-time setup`);
    }
    const fees = parts.join(' + ');
    if (fees && assoc.feeNote) {
        return `${fees}, ${assoc.feeNote}`;
    }
    return fees || assoc.feeNote || '';
};
</script>
